import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import { LifeScoreInfo } from '../LifeScoreInfo';
import { Card } from '../ui/Card';
import { useLocale } from '../../contexts/LocaleContext';
interface SummaryStat {
  label: string;
  value: string;
  hint?: string;
}

/**
 * Compact horizontal health summary for the Home.
 * Life Score keeps the strongest weight through scale and the gold track, but
 * the full 60-tick gauge stays on Progress / Life Score Detail.
 */
export function DailySummaryCard({
  score,
  total,
  bandLabel,
  stats
}: {
  score: number;
  total: number;
  bandLabel: string;
  stats: SummaryStat[];
}) {
  const {
    t
  } = useLocale();
  const navigate = useNavigate();
  return <Card tone="dark" className="rounded-panel p-2" data-id="element-781" data-mp="1iiope4phuulj">
      <div className="flex items-center gap-1" data-id="element-782" data-mp="2pvp1yjydyen9">
        <button type="button" onClick={() => navigate('/stats/lifescore')} aria-label={t('home.summary.open')} className="flex min-w-0 flex-1 items-center gap-2 text-left" data-id="element-783" data-mp="2px05cinx5kdu">
          <span className="min-w-0 flex-1" data-id="element-784" data-mp="34jpcd6okhjuw">
            <span className="bee-overline block text-[8.5px] font-bold text-ondark" data-id="element-785" data-mp="34l0ive9ed5pp">
              {t('home.summary.lifescore')}
            </span>
            <span className="mt-[2px] flex items-baseline gap-1" data-id="element-786" data-mp="2vj10ahiqhtkn">
              <span className="bee-num text-[34px] font-bold leading-none text-sand" data-id="element-787" data-mp="2vihkb6cn9f99">
                {score}
              </span>
              <span className="bee-num text-[13px] font-medium text-ondark" data-id="element-788" data-mp="2lt7p3hmi7ggx">
                / {total}
              </span>
            </span>
          </span>
          <span className="bee-overline shrink-0 rounded-pill border border-gold/45 px-2 py-[3px] text-[8.5px] font-bold text-gold" data-id="element-789" data-mp="2guo17bocnulx">
            {bandLabel}
          </span>
          <ChevronRightIcon size={17} className="shrink-0 text-ondark" aria-hidden="true" data-id="element-790" data-mp="27tpwssa01l2t" />
        </button>
        <LifeScoreInfo tone="dark" data-id="element-791" data-mp="22vemkgfsl8k2" />
      </div>

      <div className="mt-1 h-[5px] w-full overflow-hidden rounded-pill bg-sand/15" role="img" aria-label={`${t('home.summary.lifescore')}: ${score}/${total}`} data-id="element-792" data-mp="22wppyf5bsean">
        <span className="block h-full rounded-pill bg-gold transition-[width] duration-200 ease-brand" style={{
        width: `${Math.max(2, score / total * 100)}%`
      }} data-id="element-793" data-mp="1x68crwa4pxvw" />
      </div>

      <ul className="mt-2 flex items-stretch" data-id="element-794" data-mp="1o9hjtsqviz5j">
        {stats.map((stat, index) => <li key={stat.label} className={`min-w-0 flex-1 px-2 first:pl-0 last:pr-0 ${index > 0 ? 'border-l border-sand/12' : ''}`} data-id="element-795" data-mp="2bzbv45vjofno" data-mpi={index}>
            <p className="bee-overline truncate text-[8px] font-bold text-ondark" data-id="element-796" data-mp="225bizk1uia5g">
              {stat.label}
            </p>
            <p className="bee-num mt-[3px] truncate text-[14px] font-bold text-sand" data-id="element-797" data-mp="2uw3cg9rj0kp3">
              {stat.value}
            </p>
            {stat.hint && <p className="mt-[1px] truncate text-[10px] text-ondark" data-id="element-798" data-mp="2zoe9n4uvtsdy">{stat.hint}</p>}
          </li>)}
      </ul>
    </Card>;
}